import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { Autocomplete, IconButton, InputAdornment, TextField } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import {
  aplicaCoresOneShell,
  deleteDepartamentoSelecionadoSession,
  deleteMenuAberto,
  deleteTelaAberta,
  geDepartamentoSession,
  getCodDepartamentoSession,
  getConfigAmbiente,
  getLstDepartamentosStorage,
  getTelaAbertaSession,
  setDepartamentoSelecionadoSession,
  setLstDepartamentosStorage,
  getTempSession,
  setTempSession
} from '../../../services/authentication/auth';
import { getDepartamentos, filtrarMenus } from '../../../api/autenticacao';
import imageDefault from '../../../assets/img/botao-fechar.svg';
import { useTelasSemMenu } from '../../../hooks/TelasSemMenu';
import { useMenuSidebar } from '../../../contexts/MenuSidebarContext';
import { encodeBase64UrlSafe } from '../../Utils/utils';

const useNavBarHook = ({
  setDepartamentoSelecionado,
  departamentoSelecionado,
  setMenuOpen,
  menuOpen,
  setEscopoAcesso
}) => {
  aplicaCoresOneShell();
  const navigate = useNavigate();
  const config = getConfigAmbiente();
  const logo =
    config && config.imagens && config.imagens.LOGO && config.imagens.LOGO !== 'image-default'
      ? config.imagens.LOGO
      : imageDefault;

  const [mostrarInputPesquisa, setMostrarInputPesquisa] = useState(false);
  const [lstItensDepartamento, setLstItensDepartamento] = useState([]);
  const [isPendingDepartamento, setIsPendingDepartamento] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [menuDepartamentoSideBar, setMenuDepartamentoSideBar] = useState(false);
  const {
    valueTab,
    setValueTab,
    controleItensVisiveis,
    setBtnMaisClickado,
    btnMaisClickado,
    esconderMenuPrincipal,
    isPendingMenuPrincipal
  } = useMenuSidebar();

  const itensVisiveis = lstItensDepartamento.slice(0, controleItensVisiveis);
  const itensMais = lstItensDepartamento.slice(controleItensVisiveis);
  const isTelaSemMenu = useTelasSemMenu();
  const location = useLocation();

  const atalhoPesquisaGeral = () => {
    const handleKeyDown = e => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const ctrlKey = isMac ? e.metaKey : e.ctrlKey;

      if (ctrlKey && e.shiftKey && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        setMostrarInputPesquisa(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  };

  const buscaListaDepartamentos = () => {
    setIsPendingDepartamento(true);

    getDepartamentos()
      .then(dados => {
        setLstItensDepartamento(dados.data);
        setLstDepartamentosStorage(dados.data);
      })
      .catch(() => {})
      .finally(() => {
        setIsPendingDepartamento(false);
      });
  };

  const handleChangeTabs = (event, newValue, id) => {
    setMostrarInputPesquisa(false);
    setMenuOpen(true);

    const isComId = Boolean(id);
    const controle = isComId ? controleItensVisiveis : 0;
    const tabIndex = isComId ? false : newValue;
    setValueTab(tabIndex);

    const opcaoSelecionada = lstItensDepartamento[newValue + controle];
    setDepartamentoSelecionadoSession(opcaoSelecionada);
    setDepartamentoSelecionado(opcaoSelecionada.id);
  };

  const mostraPesquisaGeral = () => {
    setMostrarInputPesquisa(true);
  };

  const fecharInputPesquisa = () => {
    setMostrarInputPesquisa(false);
  };

  const allProps = index => ({
    id: `simple-tab-${index}`,
    className: 'tab-nav-bar',
    'aria-controls': `simple-tabpanel-${index}`
  });

  const verificaLstSessionStorage = () => {
    const lstDepartamentosSessionStorage = getLstDepartamentosStorage();
    if (lstDepartamentosSessionStorage && lstDepartamentosSessionStorage.length > 0) {
      setLstItensDepartamento(lstDepartamentosSessionStorage);
    } else {
      buscaListaDepartamentos();
    }
  };

  const debouncedFetch = useMemo(
    () =>
      debounce(async term => {
        setLoading(true);
        try {
          const res = await filtrarMenus(term);
          setMenus(res.data);
        } finally {
          setLoading(false);
        }
      }, 300),
    []
  );

  useEffect(() => {
    verificaLstSessionStorage();
    atalhoPesquisaGeral();
  }, []);

  useEffect(() => {
    if (lstItensDepartamento && lstItensDepartamento.length > 0) {
      verificaDepartamentoSessionStorage();
    }
  }, [lstItensDepartamento]);

  useEffect(() => {
    if (inputValue) {
      debouncedFetch(inputValue);
    } else {
      setMenus([]);
    }
    return () => {
      debouncedFetch.cancel();
    };
  }, [inputValue, debouncedFetch]);

  const verificaDepartamentoSessionStorage = () => {
    const codDepartamento = getCodDepartamentoSession();
    if (codDepartamento) {
      if (lstItensDepartamento && lstItensDepartamento.length > 0) {
        const index = lstItensDepartamento.findIndex(
          dep => parseInt(dep.id) === parseInt(codDepartamento)
        );

        if (index < controleItensVisiveis) {
          if (index >= 0) {
            setValueTab(index);
          }
        }
        setDepartamentoSelecionado(codDepartamento);
      }
    }
  };

  const encontrarIndicePorId = idProcurado =>
    lstItensDepartamento.findIndex(item => item.id === idProcurado);

  const verificaMenuAberto = () => {
    if (menuOpen) {
      return;
    }

    const telaAtual = getTelaAbertaSession();
    const telaAbertaTemp = getTempSession('telaAbertaTemp');

    if (!telaAtual) {
      deleteDepartamentoSelecionadoSession();
      setDepartamentoSelecionado(false);
      setBtnMaisClickado(false);
      setValueTab(false);
      return;
    }

    if (telaAtual && telaAtual !== telaAbertaTemp) {
      setTempSession('codDepartamentoTemp', departamentoSelecionado);
      setTempSession('departamentoTemp', geDepartamentoSession());
      setTempSession('btnMaisClickadoTemp', btnMaisClickado);
      setTempSession('telaAbertaTemp', telaAtual);
      return;
    }

    const codDepartamentoTemp = getTempSession('codDepartamentoTemp');
    const departamentoTemp = getTempSession('departamentoTemp');
    const btnMaisClickadoTemp = getTempSession('btnMaisClickadoTemp');

    if (codDepartamentoTemp && codDepartamentoTemp !== departamentoSelecionado) {
      setDepartamentoSelecionado(codDepartamentoTemp);
      setBtnMaisClickado(btnMaisClickadoTemp);
      const indice = encontrarIndicePorId(codDepartamentoTemp);
      setValueTab(indice >= 0 && indice < controleItensVisiveis ? indice : false);
      if (departamentoTemp) {
        setDepartamentoSelecionadoSession(departamentoTemp);
      }
    }
  };

  useEffect(() => {
    if (!isTelaSemMenu) {
      verificaMenuAberto();
    }

    if (isTelaSemMenu && !menuOpen) {
      setTimeout(() => {
        esconderMenuPrincipal();
      }, 150);
    }
  }, [menuOpen]);

  const navegarParaConfiguracoes = () => {
    const telaConfiguracoes = '/configuracoes';
    if (location.pathname !== telaConfiguracoes) {
      deleteMenuAberto();
      deleteTelaAberta();
      setTimeout(() => {
        navigate(telaConfiguracoes);
      }, 150);
    }
  };

  const fecharInputDePesquisa = () => {
    setMostrarInputPesquisa(false);
    setInputValue('');
  };

  const AutoCompleteCustom = () => (
    <Autocomplete
      freeSolo
      disableClearable
      open={Boolean(inputValue)}
      loading={loading || menus.length <= 0}
      loadingText={loading ? 'Carregando...' : 'Nenhum resultado encontrado.'}
      inputValue={inputValue}
      options={menus}
      onInputChange={(_, newVal) => {
        setInputValue(newVal);
        if (newVal === '' || menus.length === 0) {
          setMenus([]);
          setLoading(false);
        } else {
          debouncedFetch(newVal);
        }
      }}
      getOptionLabel={opt =>
        typeof opt === 'string'
          ? opt
          : `${opt.CATEGORIA} → ${opt.MENU} → ${opt.IDSUBMENU} - ${opt.SUBMENU}`
      }
      onBlur={fecharInputDePesquisa}
      renderOption={(props, option, { inputValue }) => {
        const { key, className, ...optionProps } = props;
        const fullLabel = `${option.CATEGORIA} → ${option.MENU} → ${option.IDSUBMENU} - ${option.SUBMENU}`;
        const matches = match(fullLabel, inputValue, { insideWords: true });
        const parts = parse(fullLabel, matches);
        return (
          <Link
            to={`/iframe/${encodeBase64UrlSafe(option.CODDEPARTAMENTO)}/${encodeBase64UrlSafe(option.IDSUBMENU)}`}
            key={`${option.CODDEPARTAMENTO}-${option.CAMINHO}`}
            onClick={evento => {
              fecharInputDePesquisa();
            }}
            href="iframe"
            className="color-unset text-decoration-unset"
          >
            <li {...optionProps} className={`${className} list-autocomplete-geral`}>
              <div>
                {parts.map((part, index) => (
                  <span
                    key={index}
                    style={{
                      fontWeight: part.highlight ? 700 : 400
                    }}
                  >
                    {part.text}
                  </span>
                ))}
              </div>
            </li>
          </Link>
        );
      }}
      renderInput={params => (
        <TextField
          autoFocus
          {...params}
          placeholder="Pesquise por palavras-chave"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  className="icon-notificacao"
                  onClick={() => fecharInputPesquisa()}
                  edge="end"
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      )}
    />
  );

  const irParaHome = () => {
    deleteMenuAberto();
    deleteTelaAberta();
    esconderMenuPrincipal();
    navigate('/home');
  };

  return {
    isPendingDepartamento,
    mostrarInputPesquisa,
    lstItensDepartamento,
    mostraPesquisaGeral,
    fecharInputPesquisa,
    handleChangeTabs,
    valueTab,
    allProps,
    logo,
    itensVisiveis,
    itensMais,
    navegarParaConfiguracoes,
    btnMaisClickado,
    setBtnMaisClickado,
    setInputValue,
    inputValue,
    loading,
    setLoading,
    menus,
    navigate,
    isPendingMenuPrincipal,
    fecharInputDePesquisa,
    debouncedFetch,
    setMenus,
    AutoCompleteCustom,
    menuDepartamentoSideBar,
    setMenuDepartamentoSideBar,
    irParaHome
  };
};

export default useNavBarHook;
